import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { packApiService, Pack, CreatePackData } from '../services/packApi';
import { Service } from '../services/serviceApi';
import { formatDate } from '../utils/dateUtils';

interface PackManagerProps {
  services: Service[];
}

const PackManager: React.FC<PackManagerProps> = ({ services }) => {
  const [packs, setPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPack, setEditingPack] = useState<Pack | null>(null);
  const [formData, setFormData] = useState<CreatePackData>({
    name: '',
    description: '',
    total_price: 0,
    payment_facility: '',
    service_ids: [],
    is_active: true
  });

  useEffect(() => {
    loadPacks();
  }, []);

  const loadPacks = async () => {
    try {
      setLoading(true);
      setError(null);
      const packsData = await packApiService.getPacks();
      setPacks(Array.isArray(packsData) ? packsData : []);
    } catch (err: any) {
      console.error('Erreur lors du chargement des packs:', err);
      setError(err.message || 'Erreur lors du chargement des packs');
      setPacks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors = packApiService.validatePackData(formData);
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    try {
      if (editingPack) {
        await packApiService.updatePack(editingPack.id, formData);
      } else {
        await packApiService.createPack(formData);
      }
      
      await loadPacks();
      resetForm();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (pack: Pack) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le pack "${pack.name}" ?`)) {
      return;
    }

    try {
      await packApiService.deletePack(pack.id);
      await loadPacks();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleStatus = async (pack: Pack) => {
    try {
      await packApiService.togglePackStatus(pack.id);
      await loadPacks();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      total_price: 0,
      payment_facility: '',
      service_ids: [],
      is_active: true
    });
    setEditingPack(null);
    setShowAddModal(false);
  };

  const startEdit = (pack: Pack) => {
    setFormData({
      name: pack.name,
      description: pack.description || '',
      total_price: pack.total_price,
      payment_facility: pack.payment_facility || '',
      service_ids: pack.services.map(s => parseInt(s.id)),
      is_active: pack.is_active
    });
    setEditingPack(pack);
    setShowAddModal(true);
  };

  const toggleServiceSelection = (serviceId: number) => {
    setFormData(prev => ({
      ...prev,
      service_ids: prev.service_ids.includes(serviceId)
        ? prev.service_ids.filter(id => id !== serviceId)
        : [...prev.service_ids, serviceId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Packs ({packs.length})</h2>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <span>➕</span>
            <span>Ajouter un pack</span>
          </button>
        </div>
      </div>

      {/* Messages d'erreur */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
          >
            Fermer
          </button>
        </div>
      )}

      {/* Liste des packs */}
      {packs.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">📦</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun pack</h3>
          <p className="text-gray-600 mb-4">Commencez par créer votre premier pack de services.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Créer un pack
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {packs.map((pack) => (
            <motion.div
              key={pack.id}
              layout
              className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="font-semibold text-gray-900">{pack.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      pack.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {pack.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  
                  {pack.description && (
                    <p className="text-sm text-gray-600 mb-3">{pack.description}</p>
                  )}
                  
                  <div className="space-y-2 mb-3">
                    <p className="text-lg font-bold text-blue-600">{pack.price_display}</p>
                    {pack.payment_facility && (
                      <p className="text-sm text-gray-600">💳 {pack.payment_facility}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-500">Services inclus:</p>
                    <div className="flex flex-wrap gap-1">
                      {pack.services.map((service) => (
                        <span
                          key={service.id}
                          className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs"
                        >
                          {service.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex space-x-2">
                  <button
                    onClick={() => startEdit(pack)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  
                  <button
                    onClick={() => handleToggleStatus(pack)}
                    className={`p-2 rounded-lg transition-colors ${
                      pack.is_active
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-green-600 hover:bg-green-50'
                    }`}
                    title={pack.is_active ? 'Désactiver' : 'Activer'}
                  >
                    {pack.is_active ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                  
                  <button
                    onClick={() => handleDelete(pack)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
                
                <p className="text-xs text-gray-400">
                  {formatDate(pack.updated_at)}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal d'ajout/modification */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-60 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">
                    {editingPack ? 'Modifier le pack' : 'Créer un pack'}
                  </h2>
                  <button
                    onClick={resetForm}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom du pack *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: Pack Startup"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description du pack
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={3}
                      placeholder="Décrivez le pack et ses composants..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prix total (FCFA) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.total_price}
                      onChange={(e) => setFormData({ ...formData, total_price: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Facilité de paiement (optionnel)
                    </label>
                    <input
                      type="text"
                      value={formData.payment_facility}
                      onChange={(e) => setFormData({ ...formData, payment_facility: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: 3x sans frais, Paiement mensuel..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Services inclus * (sélectionnez au moins un)
                    </label>
                    <div className="border border-gray-300 rounded-lg p-3 max-h-60 overflow-y-auto">
                      {services.length === 0 ? (
                        <p className="text-gray-500 text-sm">Aucun service disponible. Créez d'abord des services.</p>
                      ) : (
                        <div className="space-y-2">
                          {services.map((service) => (
                            <label
                              key={service.id}
                              className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formData.service_ids.includes(parseInt(service.id))}
                                onChange={() => toggleServiceSelection(parseInt(service.id))}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{service.name}</p>
                                <p className="text-xs text-gray-500">{service.price_display}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                      Pack actif
                    </label>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {editingPack ? 'Modifier' : 'Créer'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PackManager;
